import {
  DanceEventScheduleCategory,
  DanceEventScheduleItem,
  DanceEventWithSchedule,
  EventPrice,
  Location,
  PageDataTypeCMSEventPage,
} from "../../types";
import CMS from ".";
import { RouteComponentProps } from "../../declaration";
import { useState } from "preact/hooks";
import { MenuItem, Select } from "@mui/material";
import {
  dateFormatAU,
  daysInRange,
  EVENT_TIME_DEFAULT,
} from "../../utils/datetime";
import {
  DanceEventScheduleTimeline,
  DanceEventScheduleTimelineKey,
  scheduleItemSort,
  timelineDayLabel,
} from "../../components/timeline";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { CalendarLinks } from "../../components/calendar_links";

export default function CMSEventPage(props: RouteComponentProps) {
  const pageData = props.innerHTML as PageDataTypeCMSEventPage;
  const eventList = pageData.CMSEventPage?.eventList || [];
  const eventNames: Record<string, string> = Object.fromEntries(
    eventList.map((eventEntry) => [eventEntry.id, eventEntry.name])
  );
  const innerHTML = pageData.body;

  const [danceEvent, setDanceEvent] = useState<
    DanceEventWithSchedule | undefined
  >(
    eventList.find(
      (eventEntry) => eventEntry.id === pageData.CMSEventPage?.eventId
    )
  );

  const setEventId = (eventId: string) => {
    const encodedPath = encodeURIComponent(props.path?.replace(/\?.*/, ""));
    updateDoc(doc(db, "html-published/" + encodedPath), { eventId }).then(() =>
      setDanceEvent(eventList.find((eventEntry) => eventEntry.id === eventId))
    );
  };

  const subProps = { ...props, innerHTML } as unknown as RouteComponentProps;
  const location = danceEvent?.location as unknown as Location | undefined;

  const isEditor =
    props.currentUser?.claims.admin || props.currentUser?.claims.editor;

  const eventSelector = () => {
    if (!isEditor) {
      return <></>;
    }
    return (
      <div
        style={{
          border: "1px solid #0003",
          padding: "1em",
          marginBottom: "2em",
        }}
      >
        <strong>Event Page</strong> includes schedule & pass prices if published
        for the event.
        <br />
        Using details from event &nbsp;
        <Select
          value={danceEvent?.id || ""}
          onChange={(e) => {
            const target = e.target as EventTarget & {
              value: string;
              name: string;
            };
            setEventId(target.value);
          }}
        >
          <MenuItem value={""}>None</MenuItem>
          {Object.entries(eventNames).map(([id, name]) => (
            <MenuItem value={id}>{name}</MenuItem>
          ))}
        </Select>
        {danceEvent && (
          <div>
            Schedule {danceEvent.show_schedule ? "" : "not"} displayed.
            <br />
            Passes {danceEvent.show_prices ? "" : "not"} displayed.
            <br />
            To update event details, go to: Admin → Classes & Events → Event
            Details
          </div>
        )}
      </div>
    );
  };

  const eventSchedule = (danceEvent: DanceEventWithSchedule) => {
    const eventStart = new Date(danceEvent.start.seconds * 1000);
    const eventEnd = danceEvent.end?.seconds
      ? new Date(danceEvent.end?.seconds * 1000)
      : undefined;
    const eventDays = daysInRange(eventStart, eventEnd);
    const scheduleItems: Record<string, DanceEventScheduleItem> =
      Object.fromEntries(danceEvent.schedule!.map((item) => [item.id, item]));
    const scheduleItemCategories: Record<string, DanceEventScheduleCategory> =
      Object.fromEntries(
        danceEvent.item_categories!.map((item) => [item.id, item])
      );

    const renderDay = (day: number) => {
      const dayScheduleItems = Object.values(scheduleItems)
        .filter((item) => item.day === day + 1)
        .sort(scheduleItemSort);

      return (
        <DanceEventScheduleTimeline
          scheduleItems={dayScheduleItems}
          itemCategories={scheduleItemCategories}
          openTime={
            (typeof day === "number" && danceEvent.open_times?.[day]) ||
            EVENT_TIME_DEFAULT
          }
        />
      );
    };

    return (
      <>
        <h2 id="schedule">Schedule</h2>
        <div
          style={{ display: "flex", gap: "1em" }}
          class={"flex-column-under-992"}
        >
          {Array.from({ length: eventDays }, (_, day) => (
            <div style={{ flex: "1 0 0" }}>
              {eventDays > 1 && timelineDayLabel(eventStart, day)}
              {renderDay(day)}
            </div>
          ))}
        </div>
        <DanceEventScheduleTimelineKey
          itemCategories={scheduleItemCategories}
          scheduleItems={Object.values(scheduleItems)}
        />
        <br />
        <br />
      </>
    );
  };

  const eventPrices = (danceEvent: DanceEventWithSchedule) => {
    const sorted = (danceEvent.prices || [])
      .filter((price) => !price.publicHidden)
      .sort((a: EventPrice, b: EventPrice) => {
        const a_until = a.available_until?.seconds || 0;
        const b_until = b.available_until?.seconds || 0;
        const until_delta = b_until - a_until;
        if (until_delta !== 0) {
          return until_delta;
        }
        const price_delta = b.full_price - a.full_price;
        if (price_delta !== 0) {
          return price_delta;
        }

        return a.name.localeCompare(b.name);
      });

    return (
      <>
        <h2 id="prices">Prices</h2>
        {sorted.map((price) => (
          <div>
            <strong>{price.name}</strong>: ${price.full_price}
            {price.concession
              ? " full / $" + price.concession + " concession"
              : ""}
            {price.available_until && (
              <>
                <br />
                Available until{" "}
                {dateFormatAU(
                  new Date(price.available_until.seconds * 1000),
                  true
                )}
              </>
            )}
            <br />
            <br />
          </div>
        ))}
        <br />
        <br />
      </>
    );
  };

  const eventLocation = (location: Location) => {
    return (
      <>
        <h2 id="location">Location</h2>
        <strong>{location.name}</strong>
        {location.description && <div>{location.description}</div>}
        <div>{location.address}</div>
        {location.map_link && (
          <div>
            <a href={location.map_link}>{location.map_link}</a>
          </div>
        )}
        <br />
        <br />
      </>
    );
  };

  const showSchedule = !!(danceEvent?.show_schedule && danceEvent.schedule);
  const showPrices = !!(danceEvent?.show_prices && danceEvent.prices);
  const showLocation = !!location?.name;

  return (
    <>
      {eventSelector()}
      <CMS {...subProps} />
      {danceEvent && <CalendarLinks event={danceEvent} location={location} />}
      {showLocation && eventLocation(location)}
      {showSchedule && eventSchedule(danceEvent)}
      {showPrices && eventPrices(danceEvent)}
      <p></p>
    </>
  );
}
